/**
 * WebSocket service for interpreter queue management.
 *
 * This client speaks the same protocol as the local `vrs-server` queue
 * backend at `/ws`.
 */
import { getUserRole } from '../base/user-role/functions';
import { getAppName, isNativeRuntime } from '../base/whitelabel/functions';
import { mobileLog } from '../mobile/navigation/logging';
import { registerAlertNotificationToken, registerCurrentNotificationDevice } from '../notifications/deviceRegistration';
import { getApiOrigin, getApiSessionIdentity, refreshAuthSession } from '../shared/api-client';
import { vrsAuthService } from '../vrs-auth/VRSSAuthService';
import { getStorageGeneration, removePersistentItem, setPersistentItem } from '../vrs-auth/storage';
import { getInterpretedStartWithVideoMuted } from './mediaDefaults';
import { queueMessageSchema } from '../../../contracts';
import { CONNECTION_ERROR_LOG_INTERVAL, PENDING_AUTHED_MESSAGE_LIMIT, RECONNECT_STABILITY_WINDOW_MS, SEND_WARNING_LOG_INTERVAL, getCurrentRoomName, getErrorMessage, getHandoffDeviceId, getPresenceHeartbeatPayload, getStoredAuthToken, getStoredJson, getVRSConfig, hasQueueAuthForRole, isAccessTokenExpired, isRecord, normalizeP2PPhoneTarget, numberValue, recordPayload, stringValue } from './queueServiceUtils';
import { normalizeCallPeers, normalizeInterpreterRequest, normalizeMatchPayload, normalizeNotificationPayload, normalizeP2PInvitePayload, normalizeQueueStatus, normalizeRequestQueued, normalizeVoicemailPayload, normalizeVriInvitePrepared } from './queueMessageNormalizers';
import { getDefaultInterpreterLanguages, getDefaultRequestLanguage } from './interpreterLanguages';
function accountCallHoldPayload(data) {
    const payload = isRecord(data) ? data : {};
    const role = payload.role === 'client' || payload.role === 'interpreter' ? payload.role : undefined;
    const roomName = stringValue(payload.roomName);
    return {
        role,
        callId: stringValue(payload.callId),
        roomName: roomName || null,
        device: stringValue(payload.device),
        since: stringValue(payload.since) || null,
        canMoveHere: payload.canMoveHere === true,
        recheckAfterMs: numberValue(payload.recheckAfterMs),
        message: stringValue(payload.message)
    };
}
function callMovePayload(data) {
    const payload = isRecord(data) ? data : {};
    return {
        callId: stringValue(payload.callId),
        roomName: stringValue(payload.roomName) || null,
        device: stringValue(payload.device),
        message: stringValue(payload.message)
    };
}
/**
 * Whether this is a web call-room page (not a portal or dashboard). Sent at
 * sign-in so an interpreter's room mid-call keeps its socket when a portal
 * tab of the same browser opens.
 */
function isWebRoomPage() {
    if (isNativeRuntime() || typeof window === 'undefined') {
        return false;
    }
    const last = window.location?.pathname?.split('/').filter(Boolean).pop();
    return Boolean(last) && !last.includes('.');
}
class InterpreterQueueService {
    constructor() {
        this.ws = null;
        this.lastSentInterpreterStatus = null;
        /**
         * Set while the interpreter's wrap-up owns the return to the queue: a
         * reconnect must not re-announce Available before the breather ends.
         */
        this.availabilityReplayHeld = false;
        this.lastInterpreterName = null;
        this.lastInterpreterLanguages = getDefaultInterpreterLanguages();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 10;
        this.baseReconnectDelay = 1000;
        this.maxReconnectDelay = 30000;
        this.stabilityTimer = null;
        this.reconnectTimeout = null;
        this.heartbeatInterval = null;
        this.listeners = new Map();
        this.userId = null;
        this.shouldReconnect = true;
        // Latched when the server supersedes this socket because the same account
        // connected from another tab on this device. While set, NOTHING may
        // reopen the socket implicitly — two tabs each auto-reconnecting take
        // turns superseding each other in an endless online/offline war. Only an
        // explicit user action (toggling availability, a fresh page load) clears
        // it.
        this.superseded = false;
        this.lastConnectionErrorLogAt = 0;
        this.connectionErrorCount = 0;
        this.lastSendWarningLogAt = 0;
        this.presenceEventCleanup = null;
        this.authRefreshAttempted = false;
        this.authenticated = false;
        this.pendingAuthedMessages = [];
        // Set while the server holds this session out because another device of
        // the same interpreter account is in a live call (session_held_elsewhere).
        // The socket stays open but unauthenticated; nothing may re-send auth on
        // its own — only an explicit retakeSession() after the call has ended.
        this.heldElsewhere = false;
        // Calls that moved to another device of this client account. This device
        // leaving them must never send call_end: the call continues elsewhere.
        this.movedAwayCallIds = new Set();
        this.conferenceResponses = new Map();
        this.conferenceResponseTimers = new Map();
        this.config = getVRSConfig();
        this.userRole = getUserRole();
        this.installPresenceEventListeners();
        if (hasQueueAuthForRole(this.userRole)) {
            this.connect();
        }
    }
    installPresenceEventListeners() {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }
        const sendPresenceSoon = () => setTimeout(() => this.ping(), 0);
        document.addEventListener('visibilitychange', sendPresenceSoon);
        window.addEventListener('focus', sendPresenceSoon);
        window.addEventListener('online', sendPresenceSoon);
        this.presenceEventCleanup = () => {
            document.removeEventListener('visibilitychange', sendPresenceSoon);
            window.removeEventListener('focus', sendPresenceSoon);
            window.removeEventListener('online', sendPresenceSoon);
        };
    }
    connect() {
        if (!this.shouldReconnect)
            return;
        this.userRole = getUserRole();
        this.config = getVRSConfig();
        if (!hasQueueAuthForRole(this.userRole)) {
            return;
        }
        if (this.superseded) {
            mobileLog('warn', 'queue_ws_connect_blocked_superseded', {}, { console: false });
            return;
        }
        if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
            return;
        }
        try {
            const wsUrl = this.config.queueServiceUrl;
            const socket = new WebSocket(wsUrl);
            this.ws = socket;
            const origin = getApiOrigin();
            const generation = getStorageGeneration();
            const ownsSocket = () => this.ws === socket && this.shouldReconnect
                && origin === getApiOrigin() && generation === getStorageGeneration();
            socket.onopen = () => {
                if (!ownsSocket())
                    return;
                // Only treat the connection as healthy — and reset the
                // reconnect backoff — after it survives a stability window.
                // Captive portals and IPS middleboxes complete the handshake
                // and then kill the socket within a second; resetting on open
                // turns exponential backoff into a tight forever-loop.
                this.clearStabilityTimer();
                this.stabilityTimer = setTimeout(() => {
                    if (!ownsSocket())
                        return;
                    this.stabilityTimer = null;
                    this.reconnectAttempts = 0;
                }, RECONNECT_STABILITY_WINDOW_MS);
                this.authRefreshAttempted = false;
                this.authenticated = false;
                this.startHeartbeat();
                this.sendAuth();
                this.emit('connection', { connected: true });
            };
            socket.onmessage = event => {
                if (!ownsSocket())
                    return;
                try {
                    const parsed = queueMessageSchema.safeParse(JSON.parse(event.data));
                    if (parsed.success) {
                        this.handleMessage(parsed.data);
                    }
                    else {
                        mobileLog('warn', 'queue_ws_contract_parse_failed', {
                            issueCount: parsed.error.issues.length
                        }, { console: false });
                    }
                }
                catch (error) {
                    console.error('Error parsing queue message:', error);
                }
            };
            socket.onclose = () => {
                if (!ownsSocket())
                    return;
                this.ws = null;
                this.clearStabilityTimer();
                this.authenticated = false;
                this.stopHeartbeat();
                this.emit('connection', { connected: false });
                if (this.shouldReconnect) {
                    this.attemptReconnect();
                }
            };
            socket.onerror = error => {
                if (!ownsSocket())
                    return;
                this.reportConnectionIssue(error);
            };
        }
        catch (error) {
            this.reportConnectionIssue(error);
            this.attemptReconnect();
        }
    }
    reportConnectionIssue(error) {
        this.connectionErrorCount++;
        const now = Date.now();
        const shouldLog = this.lastConnectionErrorLogAt === 0
            || now - this.lastConnectionErrorLogAt >= CONNECTION_ERROR_LOG_INTERVAL;
        const payload = {
            code: 'QUEUE_WS_CONNECTION_FAILED',
            errorCount: this.connectionErrorCount,
            message: getErrorMessage(error, 'Queue WebSocket connection failed'),
            retrying: this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts,
            url: this.config.queueServiceUrl
        };
        if (!shouldLog) {
            return;
        }
        this.lastConnectionErrorLogAt = now;
        mobileLog('warn', 'queue_ws_connection_failed', payload, { console: false });
        this.emit('error', payload);
    }
    clearStabilityTimer() {
        if (this.stabilityTimer) {
            clearTimeout(this.stabilityTimer);
            this.stabilityTimer = null;
        }
    }
    startHeartbeat() {
        this.stopHeartbeat();
        this.heartbeatInterval = setInterval(() => {
            this.send({ type: 'heartbeat', data: getPresenceHeartbeatPayload() });
        }, 15000);
    }
    stopHeartbeat() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
            this.heartbeatInterval = null;
        }
    }
    attemptReconnect() {
        if (!this.shouldReconnect) {
            return;
        }
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            this.emit('connection', {
                connected: false,
                maxAttemptsReached: true,
                message: 'Unable to reach the queue server. Toggle your availability or reopen the app to retry.'
            });
            return;
        }
        this.reconnectAttempts++;
        const exponentialDelay = Math.min(this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1), this.maxReconnectDelay);
        const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
        const delay = Math.round(exponentialDelay + jitter);
        this.reconnectTimeout = setTimeout(() => {
            this.reconnectTimeout = null;
            this.connect();
        }, delay);
    }
    sendAuth() {
        const storedToken = getStoredAuthToken();
        // Use the same checked refresh outcome for startup and server rejection.
        if (storedToken?.token && isAccessTokenExpired(storedToken.token) && !this.authRefreshAttempted) {
            this.authRefreshAttempted = true;
            void this.refreshAndReauthenticate({ code: 'TOKEN_EXPIRED' });
            return;
        }
        this.sendAuthMessage(storedToken?.token);
    }
    authReplyIsCurrent(socket, identity, origin, token) {
        return this.ws === socket && this.shouldReconnect && getApiOrigin() === origin
            && (token ? getStoredAuthToken()?.token === token : getApiSessionIdentity() === identity);
    }
    sendAuthMessage(token) {
        const storedToken = getStoredAuthToken();
        const storedUser = getStoredJson('vrs_user_info');
        const fallbackUserId = storedUser?.id || storedToken?.userId || `${this.userRole}-${Date.now()}`;
        const fallbackName = storedUser?.name || storedToken?.name || (this.userRole === 'interpreter' ? 'Interpreter' : 'Guest');
        this.send({
            type: 'auth',
            role: this.userRole,
            userId: fallbackUserId,
            name: fallbackName,
            token,
            deviceId: getHandoffDeviceId(),
            ...(isWebRoomPage() ? { surface: 'room' } : {})
        });
    }
    /**
     * Recovers from a server-side auth rejection (expired token or revoked
     * session id) by minting a fresh access token via the refresh endpoint
     * and re-authenticating the live socket. Terminal credential failures
     * return native users to sign-in; non-auth errors remain visible in place.
     */
    async refreshAndReauthenticate(originalError) {
        const socket = this.ws;
        const identity = getApiSessionIdentity();
        const origin = getApiOrigin();
        const generation = getStorageGeneration();
        const result = await refreshAuthSession();
        const token = result.status === 'refreshed' ? result.token : undefined;
        if (generation !== getStorageGeneration() || !this.authReplyIsCurrent(socket, identity, origin, token)
            || result.status === 'superseded')
            return;
        if (result.status === 'refreshed') {
            mobileLog('info', 'queue_ws_auth_refreshed', {}, { console: false });
            this.sendAuthMessage(result.token);
            return;
        }
        if (result.status === 'unavailable') {
            this.authenticated = false;
            this.closeSocket();
            this.clearStabilityTimer();
            this.stopHeartbeat();
            this.emit('connection', { connected: false });
            this.emit('error', { code: 'AUTH_REFRESH_UNAVAILABLE', retrying: this.reconnectAttempts < this.maxReconnectAttempts,
                message: 'Your connection could not be renewed. Reconnecting…' });
            this.attemptReconnect();
            return;
        }
        if (result.status === 'rejected') {
            const { code } = result;
            const failure = { ...originalError, code };
            mobileLog('warn', 'queue_ws_auth_refresh_rejected', { code }, { console: false });
            this.shouldReconnect = false;
            const native = isNativeRuntime();
            if (native)
                this.emit('sessionRevoked', failure);
            try {
                vrsAuthService.logout();
            }
            catch {
                mobileLog('warn', 'queue_ws_terminal_auth_cleanup_failed', { code }, { console: false });
            }
            if (!native)
                this.emit('sessionRevoked', failure);
        }
    }
    registerNotificationDevice() {
        const storedToken = getStoredAuthToken();
        if (!storedToken?.token) {
            return;
        }
        void registerCurrentNotificationDevice().then(async (response) => {
            if (response.error) {
                mobileLog('warn', 'notification_device_registration_failed', {
                    status: response.status
                }, { console: false });
            }
            // iOS meeting reminders ride a second (alert) token; never prompts here.
            if (isNativeRuntime() && getUserRole() === 'client')
                await registerAlertNotificationToken(false);
        }).catch(() => {
            mobileLog('warn', 'notification_device_registration_failed', {
                message: 'Notification device registration is not confirmed'
            }, { console: false });
        });
    }
    showBrowserNotification(data) {
        const windowRef = typeof window !== 'undefined'
            ? window
            : undefined;
        const NotificationRef = windowRef?.Notification;
        if (!windowRef || !NotificationRef || NotificationRef.permission !== 'granted') {
            return;
        }
        try {
            const notification = new NotificationRef(data.title || getAppName(), {
                body: data.body,
                data: data.data
            });
            notification.onclick = () => {
                windowRef.focus?.();
                const roomName = isRecord(data.data) ? stringValue(data.data.roomName) : undefined;
                if (roomName) {
                    const clientStartWithVideoMuted = isRecord(data.data)
                        && typeof data.data.clientStartWithVideoMuted === 'boolean'
                        ? data.data.clientStartWithVideoMuted
                        : undefined;
                    const startWithVideoMuted = getInterpretedStartWithVideoMuted(getUserRole(), clientStartWithVideoMuted);
                    windowRef.location.assign(`/${roomName}#config.startWithVideoMuted=${startWithVideoMuted}`);
                }
            };
        }
        catch {
            // Browser notification support is best-effort; server push remains authoritative.
        }
    }
    handleMessage(message) {
        switch (message.type) {
            case 'connected':
                this.userId = message.clientId || null;
                break;
            case 'auth_success': {
                const hadQueuedAvailability = this.pendingAuthedMessages.some(pending => pending.type === 'interpreter_status');
                this.authRefreshAttempted = false;
                this.authenticated = true;
                this.heldElsewhere = false;
                this.userId = message.userId
                    || getStoredJson('vrs_user_info')?.id
                    || this.userId;
                this.flushPendingAuthedMessages();
                this.emit('authenticated', {
                    role: message.role,
                    clientId: message.clientId,
                    capabilities: isRecord(message.capabilities)
                        ? {
                            calendarReminders: message.capabilities.calendarReminders === true,
                            externalMeetingVrs: message.capabilities.externalMeetingVrs === true
                        }
                        : undefined
                });
                this.registerNotificationDevice();
                if (this.userRole === 'interpreter'
                    && this.lastSentInterpreterStatus === 'active'
                    && !this.availabilityReplayHeld
                    && !hadQueuedAvailability
                    && !this.superseded
                    && !getStoredJson('vrs_active_call')?.roomName) {
                    const profile = getStoredJson('vrs_user_info');
                    this.sendInterpreterStatus('active', this.lastInterpreterName || profile?.name, this.lastInterpreterLanguages, this.lastInterpreterServiceModes || profile?.serviceModes);
                    mobileLog('info', 'queue_availability_restored_after_auth', {
                        languages: this.lastInterpreterLanguages
                    }, { console: false });
                }
                this.ping();
                break;
            }
            case 'queue_status':
                this.emit('queueStatus', normalizeQueueStatus(message.data));
                break;
            case 'match_found':
                this.emit('matchFound', normalizeMatchPayload(message.data));
                break;
            case 'request_assigned':
                this.emit('requestAssigned', normalizeMatchPayload(message.data));
                break;
            case 'request_queued':
                this.emit('requestQueued', normalizeRequestQueued(message.data));
                break;
            case 'request_cancelled':
                this.emit('requestCancelled', normalizeRequestQueued(message.data));
                break;
            /*
             * The interpreter called a client rather than waiting for one.
             *
             * Room entry is not handled here: the server sends
             * `meeting_initiated` immediately after the started event, and the
             * existing match path takes the interpreter into the room. These
             * two only carry what to say while that happens, and what to say
             * when it does not.
             */
            case 'interpreter_client_call_started': {
                const payload = isRecord(message.data) ? message.data : {};
                this.emit('interpreterClientCallStarted', {
                    callId: String(payload.callId || ''),
                    clientName: String(payload.clientName || ''),
                    roomName: String(payload.roomName || '')
                });
                break;
            }
            case 'interpreter_client_call_failed': {
                const payload = isRecord(message.data) ? message.data : {};
                this.emit('interpreterClientCallFailed', {
                    code: String(payload.code || ''),
                    message: String(payload.message || 'The client call could not be started.')
                });
                break;
            }
            // The request left the queue (client cancelled, timed out, or
            // another interpreter took it) — same dismissal path as an
            // explicit cancellation so delivered popups clear themselves.
            case 'request_withdrawn':
                this.emit('requestCancelled', normalizeRequestQueued(message.data));
                break;
            case 'interpreter_request':
                this.emit('interpreterRequest', normalizeInterpreterRequest(message.data));
                break;
            case 'request_accepted':
                this.emit('requestAccepted', normalizeMatchPayload(message.data));
                break;
            case 'request_declined':
                this.emit('requestDeclined', normalizeMatchPayload(message.data));
                break;
            case 'meeting_reminder':
                if (isRecord(message.data)) {
                    this.emit('meetingReminder', { reminders: Array.isArray(message.data.reminders) ? message.data.reminders : [] });
                }
                break;
            case 'external_meeting_progress':
                if (isRecord(message.data)) {
                    this.emit('externalMeetingProgress', {
                        callId: stringValue(message.data.callId),
                        event: message.data.event === 'entered' || message.data.event === 'identified'
                            ? message.data.event : undefined,
                        platform: stringValue(message.data.platform)
                    });
                }
                break;
            case 'meeting_initiated':
                this.emit('meetingInitiated', normalizeMatchPayload(message.data));
                break;
            case 'call_ended':
                this.clearConferenceResponsesForCall(normalizeMatchPayload(message.data).callId);
                this.emit('callEnded', normalizeMatchPayload(message.data));
                break;
            case 'client_rering_sent':
                this.emit('clientReringSent', isRecord(message.data) ? message.data : {});
                break;
            case 'client_rering_skipped':
                // The client's calling browser already has this room open; a
                // ring would only reach the account's other devices.
                this.emit('clientReringSkipped', isRecord(message.data) ? message.data : {});
                break;
            case 'ping':
                this.send({ type: 'heartbeat' });
                break;
            case 'pong':
            case 'heartbeat_ack':
                break;
            case 'status_updated':
                this.emit('statusUpdated', isRecord(message.data) ? message.data : message);
                break;
            case 'session_superseded': {
                // The account signed in elsewhere and the server closed this
                // socket. Do NOT reconnect — otherwise the old and new tabs
                // would take turns superseding each other in an endless loop.
                const payload = isRecord(message.data) ? message.data : { message: 'Signed in on another device.' };
                this.shouldReconnect = false;
                this.superseded = true;
                mobileLog('warn', 'queue_ws_session_superseded', {
                    message: typeof payload.message === 'string' ? payload.message : undefined
                }, { console: false });
                this.emit('sessionSuperseded', payload);
                break;
            }
            case 'session_held_elsewhere': {
                // Not an auth failure and not a sign-out: this interpreter
                // account is in a live call on another device, so the server
                // holds this session out and keeps the socket open. Stay put
                // and let the user decide once that call ends.
                const payload = accountCallHoldPayload(message.data);
                this.heldElsewhere = true;
                this.authenticated = false;
                mobileLog('warn', 'queue_ws_session_held_elsewhere', {
                    role: payload.role,
                    recheckAfterMs: payload.recheckAfterMs
                }, { console: false });
                this.emit('sessionHeldElsewhere', payload);
                break;
            }
            case 'account_call_status': {
                const payload = accountCallHoldPayload(message.data);
                this.emit('accountCallStatus', { ...payload, held: isRecord(message.data) && message.data.held === true });
                break;
            }
            case 'call_active_elsewhere':
                this.emit('callActiveElsewhere', accountCallHoldPayload(message.data));
                break;
            case 'call_moving_elsewhere':
                this.emit('callMovingElsewhere', callMovePayload(message.data));
                break;
            case 'call_moved_elsewhere': {
                const payload = callMovePayload(message.data);
                if (payload.callId) {
                    this.movedAwayCallIds.add(payload.callId);
                }
                mobileLog('info', 'queue_call_moved_elsewhere', { hasCallId: Boolean(payload.callId) }, { console: false });
                this.emit('callMovedElsewhere', payload);
                break;
            }
            case 'call_move_lapsed':
                this.emit('callMoveLapsed', callMovePayload(message.data));
                break;
            case 'call_end_ignored': {
                const payload = isRecord(message.data) ? message.data : {};
                this.emit('callEndIgnored', {
                    callId: stringValue(payload.callId),
                    reason: stringValue(payload.reason),
                    message: stringValue(payload.message)
                });
                break;
            }
            case 'admin_force_logout': {
                // An admin kicked this account. Confirmed on device 2026-08-02:
                // the web surfaces signed out immediately while Android stayed
                // happily signed in, because nothing here handled the message
                // and the socket simply reconnected on its normal timer with a
                // still-valid access token.
                const payload = isRecord(message.data) ? message.data : {};
                this.shouldReconnect = false;
                mobileLog('warn', 'queue_ws_admin_force_logout', {
                    message: typeof payload.message === 'string' ? payload.message : undefined
                }, { console: false });
                // Native listeners retire media and capture the original
                // credential before shared cleanup disconnects this socket.
                const native = isNativeRuntime();
                if (native)
                    this.emit('adminForceLogout', payload);
                try {
                    vrsAuthService.logout();
                }
                catch {
                    mobileLog('warn', 'queue_ws_admin_force_logout_cleanup_failed', {}, { console: false });
                }
                if (!native)
                    this.emit('adminForceLogout', payload);
                break;
            }
            case 'auth_error': {
                const payload = isRecord(message.data) ? message.data : { message: 'Queue server error' };
                const code = typeof payload.code === 'string' ? payload.code : undefined;
                const refreshable = code === 'TOKEN_EXPIRED' || code === 'SESSION_REVOKED' || code === 'INVALID_TOKEN' || code === undefined;
                mobileLog('warn', 'queue_ws_auth_error', {
                    code,
                    refreshable,
                    message: typeof payload.message === 'string' ? payload.message : undefined
                }, { console: false });
                // An expired/stale access token is recoverable: mint a fresh one
                // with the stored refresh token and re-authenticate, instead of
                // leaving the socket unauthenticated (which surfaces to users as
                // being kicked out / "request interpreter" failing).
                if (refreshable && !this.authRefreshAttempted) {
                    this.authRefreshAttempted = true;
                    void this.refreshAndReauthenticate(payload);
                    break;
                }
                this.emit('error', payload);
                break;
            }
            case 'error': {
                const payload = isRecord(message.data) ? message.data : { message: 'Queue server error' };
                mobileLog('warn', 'queue_ws_server_error', {
                    code: typeof payload.code === 'string' ? payload.code : undefined,
                    message: typeof payload.message === 'string' ? payload.message : undefined
                }, { console: false });
                this.emit('error', payload);
                break;
            }
            // A message we sent failed the server's schema validation. Silently
            // dropping this turned bad payloads (e.g. a contact with a
            // malformed stored number in a group call) into dead taps.
            case 'validation_error': {
                const payload = isRecord(message.data) ? message.data : {};
                mobileLog('warn', 'queue_ws_validation_error', {
                    message: typeof payload.message === 'string' ? payload.message : undefined
                }, { console: false });
                this.emit('error', {
                    ...payload,
                    code: 'VALIDATION_ERROR',
                    message: typeof payload.message === 'string' && payload.message
                        ? payload.message
                        : 'The server rejected the request format.'
                });
                break;
            }
            // Handoff message types — emitted as events for device-handoff middleware
            case 'handoff_in_progress':
                this.emit('handoff_in_progress', message.data);
                break;
            case 'handoff_complete':
                this.emit('handoff_complete', message.data);
                break;
            case 'handoff_executed':
                this.emit('handoff_executed', message.data);
                break;
            case 'handoff_consumed':
                this.emit('handoff_consumed', message.data);
                break;
            case 'handoff_release':
                this.emit('handoff_release', message.data);
                break;
            case 'handoff_cancelled':
                this.emit('handoff_cancelled', message.data);
                break;
            case 'handoff_prepared':
                this.emit('handoff_prepared', message.data);
                break;
            case 'handoff_offer':
                this.emit('handoff_offer', message.data);
                break;
            case 'handoff_devices_updated':
                this.emit('handoff_devices_updated', message.data);
                break;
            case 'handoff_error':
                this.emit('handoff_error', message.data);
                break;
            case 'session_registered':
                this.emit('session_registered', message.data);
                break;
            case 'session_unregistered':
                this.emit('session_unregistered', message.data);
                break;
            // Call Management & UX
            case 'p2p_incoming_call':
                this.emit('p2pIncomingCall', normalizeP2PInvitePayload(message.data));
                break;
            case 'inbound_vrs_call':
                // A hearing party dialed this client's VRS number. Same
                // surfaces as a P2P ring: overlay + notification.
                this.emit('inboundVrsCall', recordPayload(message.data));
                break;
            case 'p2p_incoming_call_waiting':
                this.emit('callWaitingIncoming', message.data);
                break;
            case 'call_waiting_responded':
                this.emit('callWaitingResponded', message.data);
                break;
            case 'call_on_hold':
                this.emit('callOnHold', message.data);
                break;
            case 'call_off_hold':
                this.emit('callOffHold', message.data);
                break;
            case 'call_hold_updated':
                this.emit('callHoldUpdated', message.data);
                break;
            case 'call_transfer_initiated':
                this.emit('callTransferInitiated', message.data);
                break;
            case 'call_transfer_pending':
                this.emit('callTransferPending', message.data);
                break;
            case 'call_transfer_accepted':
                this.emit('callTransferAccepted', message.data);
                break;
            case 'call_transfer_cancelled':
                this.emit('callTransferCancelled', message.data);
                break;
            case 'conference_invite':
                this.emit('p2pIncomingCall', normalizeP2PInvitePayload(message.data));
                this.emit('conferenceInvite', message.data);
                break;
            case 'conference_add_ringing':
                this.emit('conferenceAddRinging', message.data);
                break;
            case 'conference_add_offline':
                this.emit('conferenceAddOffline', message.data);
                break;
            // In-call invite outcomes for the inviter (web InvitePanel rows).
            case 'conference_invite_sent': {
                const data = recordPayload(message.data);
                this.emit('conferenceInviteSent', {
                    ...data,
                    callId: stringValue(data.callId),
                    expiresAt: stringValue(data.expiresAt),
                    inviteId: stringValue(data.inviteId),
                    requested: stringValue(data.requested),
                    ringing: data.ringing === true,
                    target: data.target
                });
                break;
            }
            case 'conference_invite_result': {
                const data = recordPayload(message.data);
                this.emit('conferenceInviteResult', {
                    ...data,
                    callId: stringValue(data.callId),
                    inviteId: stringValue(data.inviteId),
                    name: stringValue(data.name),
                    status: data.status === 'accepted' || data.status === 'declined' || data.status === 'missed'
                        || data.status === 'cancelled'
                        ? data.status : undefined,
                    targetId: stringValue(data.targetId)
                });
                break;
            }
            case 'p2p_group_no_answer': {
                const data = recordPayload(message.data);
                this.emit('p2pGroupNoAnswer', {
                    callId: stringValue(data.callId),
                    declined: normalizeCallPeers(data.declined),
                    missed: normalizeCallPeers(data.missed),
                    roomName: stringValue(data.roomName)
                });
                break;
            }
            // A missed group ring whose call is still live: the invitee may
            // still join by accepting again (VRS Home).
            case 'p2p_room_still_active': {
                const data = recordPayload(message.data);
                this.emit('p2pRoomStillActive', {
                    callId: stringValue(data.callId),
                    callerId: stringValue(data.callerId),
                    callerName: stringValue(data.callerName),
                    groupCall: data.groupCall === true,
                    participantCount: numberValue(data.participantCount),
                    roomName: stringValue(data.roomName)
                });
                break;
            }
            case 'p2p_participant_joined':
            case 'p2p_participant_left': {
                const data = recordPayload(message.data);
                this.emit(message.type === 'p2p_participant_joined' ? 'p2pParticipantJoined' : 'p2pParticipantLeft', {
                    callId: stringValue(data.callId),
                    name: stringValue(data.name),
                    participantCount: numberValue(data.participantCount),
                    rejoined: data.rejoined === true,
                    removed: data.removed === true,
                    userId: stringValue(data.userId)
                });
                break;
            }
            case 'conference_removed':
                this.clearConferenceResponsesForCall(normalizeMatchPayload(message.data).callId);
                this.emit('conferenceRemoved', message.data);
                this.emit('callEnded', normalizeMatchPayload(message.data));
                break;
            case 'conference_participant_removed':
                this.emit('conferenceParticipantRemoved', message.data);
                break;
            case 'chat_message':
                this.emit('chatMessage', message.data);
                break;
            case 'chat_message_sent':
                this.emit('chatMessageSent', message.data);
                break;
            case 'chat_history':
                this.emit('chatHistory', message.data);
                break;
            case 'preferences_updated':
                this.emit('preferencesUpdated', message.data);
                break;
            case 'p2p_target_dnd':
                this.emit('p2pTargetDnd', recordPayload(message.data));
                break;
            case 'p2p_accepted':
                this.emit('p2pAccepted', normalizeP2PInvitePayload(message.data));
                break;
            case 'p2p_declined':
                this.emit('p2pDeclined', normalizeP2PInvitePayload(message.data));
                break;
            case 'p2p_call_cancelled':
                this.emit('p2pCallCancelled', recordPayload(message.data));
                break;
            case 'p2p_call_ended':
                this.emit('p2pCallEnded', normalizeP2PInvitePayload(message.data));
                break;
            case 'conference_join_room': {
                const invitation = normalizeP2PInvitePayload(message.data);
                if (invitation.callId && this.conferenceResponses.get(`${invitation.callId}:${invitation.invitationId}`) === true) {
                    this.clearConferenceResponseTimer(`${invitation.callId}:${invitation.invitationId}`);
                    this.conferenceResponses.delete(`${invitation.callId}:${invitation.invitationId}`);
                    this.emit('p2pJoinRoom', invitation);
                }
                break;
            }
            case 'conference_invitation_error': {
                const data = recordPayload(message.data);
                const key = `${data.callId}:${data.invitationId}`;
                this.clearConferenceResponseTimer(key);
                if (data.code !== 'INVITATION_FAILED')
                    this.conferenceResponses.delete(key);
                this.emit('conferenceInvitationError', {
                    invitationId: typeof data.invitationId === 'string' ? data.invitationId : undefined,
                    callId: typeof data.callId === 'string' ? data.callId : undefined,
                    code: typeof data.code === 'string' ? data.code : undefined,
                    message: typeof data.message === 'string' ? data.message : undefined
                });
                break;
            }
            case 'conference_invitation_update': {
                const data = recordPayload(message.data);
                // This device still needs the canonical room acknowledgement. An account-wide
                // accepted update must not dismiss its retry UI if that acknowledgement was lost.
                if (data.status === 'accepted' && this.conferenceResponses.get(`${data.callId}:${data.invitationId}`) === true)
                    break;
                this.emit('conferenceInvitationUpdate', {
                    invitationId: typeof data.invitationId === 'string' ? data.invitationId : undefined,
                    callId: typeof data.callId === 'string' ? data.callId : undefined,
                    participantId: typeof data.participantId === 'string' ? data.participantId : undefined,
                    status: data.status === 'accepted' || data.status === 'declined' || data.status === 'unavailable'
                        || data.status === 'expired' ? data.status : undefined
                });
                break;
            }
            case 'p2p_join_room':
                this.emit('p2pJoinRoom', normalizeP2PInvitePayload(message.data));
                break;
            case 'p2p_ringing': {
                const ringing = isRecord(message.data) ? message.data : {};
                const delivery = ringing.delivery;
                this.emit('p2pRinging', {
                    ...normalizeP2PInvitePayload(message.data),
                    calleeId: stringValue(ringing.calleeId),
                    calleeName: stringValue(ringing.calleeName),
                    delivery: delivery === 'socket' || delivery === 'push' || delivery === 'unconfirmed'
                        ? delivery
                        : undefined,
                    deliveryReason: stringValue(ringing.deliveryReason),
                    dnd: normalizeCallPeers(ringing.dnd),
                    invited: normalizeCallPeers(ringing.invited),
                    offline: normalizeCallPeers(ringing.offline),
                    unresolved: Array.isArray(ringing.unresolved) ? ringing.unresolved : undefined
                });
                break;
            }
            case 'p2p_call_failed':
                this.emit('p2pCallFailed', recordPayload(message.data));
                break;
            case 'p2p_interpreter_requested': {
                // The contract requires callId (contracts/queue.ts); the server sends
                // { callId, requestId }. Reject a payload without it rather than
                // emitting an event the listeners cannot act on.
                const data = recordPayload(message.data);
                if (typeof data.callId !== 'string' || !data.callId) {
                    console.warn('p2p_interpreter_requested without callId:', message);
                    break;
                }
                this.emit('p2pInterpreterRequested', { ...data, callId: data.callId });
                break;
            }
            case 'p2p_interpreter_joined':
                this.emit('p2pInterpreterJoined', recordPayload(message.data));
                break;
            case 'p2p_interpreter_unavailable':
                this.emit('p2pInterpreterUnavailable', recordPayload(message.data));
                break;
            case 'p2p_target_offline':
                this.emit('p2pTargetOffline', recordPayload(message.data));
                this.emit('p2p_target_offline', recordPayload(message.data));
                break;
            case 'vrs_client_unavailable':
                this.emit('vrsClientUnavailable', recordPayload(message.data));
                break;
            case 'vrs_client_reconnecting':
                this.emit('vrsClientReconnecting', recordPayload(message.data));
                break;
            case 'notification_event':
                {
                    const payload = normalizeNotificationPayload(message.data);
                    this.showBrowserNotification(payload);
                    this.emit('notificationEvent', payload);
                }
                break;
            case 'voicemail_new_message':
                this.emit('voicemail_new_message', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_unread_count':
                this.emit('voicemail_unread_count', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_recording_started':
                this.emit('voicemail_recording_started', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_recording_complete':
                this.emit('voicemail_recording_complete', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_recording_cancelled':
                this.emit('voicemail_recording_cancelled', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_message_deleted':
                this.emit('voicemail_message_deleted', normalizeVoicemailPayload(message.data));
                break;
            case 'voicemail_error':
                this.emit('voicemail_error', normalizeVoicemailPayload(message.data));
                break;
            case 'vri_invite_prepared':
                this.emit('vriInvitePrepared', normalizeVriInvitePrepared(message.data));
                break;
            case 'contacts_changed':
                this.emit('contactsChanged', message.data);
                break;
            default:
                console.warn('Unknown queue message type:', message.type, message);
        }
    }
    on(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }
    off(event, callback) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            const index = eventListeners.indexOf(callback);
            if (index > -1) {
                eventListeners.splice(index, 1);
            }
        }
    }
    /**
     * Locally announce a room join through the same event the server's
     * p2p_join_room message would fire. Used by the inbound-VRS answer path,
     * where there is no p2p call to accept — the join is client-initiated.
     */
    announceLocalRoomJoin(data) {
        this.emit('p2pJoinRoom', data);
    }
    emit(event, data) {
        const eventListeners = this.listeners.get(event);
        if (eventListeners) {
            [...eventListeners].forEach(callback => {
                try {
                    callback(data);
                }
                catch (error) {
                    console.error(`Error in event listener for ${event}:`, error);
                }
            });
        }
    }
    updateInterpreterStatus(status, name, languages = getDefaultInterpreterLanguages(), serviceModes) {
        // A deliberate availability change is explicit user intent to own the
        // queue session in THIS tab — the one case that may take the session
        // back from whichever tab superseded it.
        if (this.superseded) {
            this.superseded = false;
            this.shouldReconnect = true;
            this.reconnectAttempts = 0;
        }
        this.availabilityReplayHeld = false;
        this.lastSentInterpreterStatus = status;
        this.lastInterpreterName = name || this.lastInterpreterName;
        this.lastInterpreterLanguages = languages;
        this.lastInterpreterServiceModes = serviceModes;
        this.sendInterpreterStatus(status, name, languages, serviceModes);
    }
    sendInterpreterStatus(status, name, languages = getDefaultInterpreterLanguages(), serviceModes) {
        this.sendWhenAuthenticated({
            type: 'interpreter_status',
            data: {
                status,
                name,
                languages,
                serviceModes
            }
        });
    }
    /**
     * After a call the server leaves the interpreter out of the queue until
     * this client says otherwise. While the wrap-up decides when that is, a
     * reconnect must not replay the Available they held before the call. Any
     * explicit availability change (updateInterpreterStatus) ends the hold.
     */
    holdAvailabilityReplay() {
        this.availabilityReplayHeld = true;
    }
    /**
     * The last availability this client asked the server for. Lets UI that
     * mounts later (e.g. the in-call status toggle) reflect a choice made
     * elsewhere, such as going active on the dashboard.
     */
    getLastInterpreterStatus() {
        return this.lastSentInterpreterStatus;
    }
    requestInterpreter(language = getDefaultRequestLanguage(), clientName, roomNameOrOptions) {
        const options = typeof roomNameOrOptions === 'string'
            ? { roomName: roomNameOrOptions }
            : roomNameOrOptions || {};
        mobileLog('info', 'queue_request_interpreter_send_attempt', {
            authenticated: this.authenticated,
            callType: options.callType || 'vrs',
            hasCallerIdPhone: Boolean(options.callerIdPhone),
            hasTargetPhone: Boolean(options.targetPhone),
            vcoEnabled: Boolean(options.vcoEnabled),
            queueConnected: this.isConnected(),
            wsReadyState: this.ws?.readyState ?? null
        }, { console: false });
        // Matches broadcast to every signed-in device of this account; only
        // the device that placed the request may auto-enter the room. Others
        // ring instead of silently hijacking the call (the phone-vs-laptop
        // supersede war that flashed cameras on and off).
        setPersistentItem('vrs_own_queue_request', JSON.stringify({ at: Date.now() }));
        this.sendWhenAuthenticated({
            type: 'request_interpreter',
            data: {
                callType: options.callType,
                callerIdPhone: options.callerIdPhone,
                corporateAccountId: options.corporateAccountId,
                costCenter: options.costCenter,
                department: options.department,
                industryTag: options.industryTag,
                instructions: options.instructions,
                inviteTokens: options.inviteTokens,
                participants: options.participants,
                language,
                clientName,
                roomName: options.roomName || getCurrentRoomName(),
                scheduledSessionId: options.scheduledSessionId,
                scheduledStartAt: options.scheduledStartAt,
                targetPhone: options.targetPhone,
                vcoEnabled: options.vcoEnabled || undefined,
                // Where the call is served (docs/regional-queue-routing.md).
                serviceRegion: options.serviceRegion || undefined,
                externalMeetingUrl: options.externalMeetingUrl || undefined,
                externalMeetingReminderId: options.externalMeetingReminderId || undefined
            }
        });
    }
    cancelRequest(requestId) {
        this.sendWhenAuthenticated({
            type: 'cancel_request',
            data: { requestId }
        });
    }
    acceptRequest(requestId, roomName, offerId) {
        this.sendWhenAuthenticated({
            type: 'accept_request',
            data: { requestId, roomName, offerId }
        });
    }
    declineRequest(requestId, offerId) {
        this.sendWhenAuthenticated({
            type: 'decline_request',
            data: { requestId, offerId }
        });
    }
    transferCall(callId, toInterpreterId, reason) {
        this.sendWhenAuthenticated({
            type: 'call_transfer',
            data: { callId, toInterpreterId, transferType: 'blind', reason: reason || 'In-call transfer' }
        });
    }
    /**
     * Re-ring the deaf client bound to this call (inbound calls where the
     * auto-ring was missed). The server resolves the target from the call
     * record — no other targets are possible.
     */
    ringClientAgain(callId) {
        this.sendWhenAuthenticated({
            type: 'ring_client_again',
            data: { callId }
        });
    }
    /** Mint an expiring guest link for the room this client is live in. */
    prepareRoomInvite(roomName, requestId, opts) {
        this.sendWhenAuthenticated({
            type: 'prepare_vri_invite',
            data: {
                roomName,
                requestId,
                ...(opts?.guestEmail ? { guestEmail: opts.guestEmail } : {}),
                ...(opts?.deliveryChannels?.length ? { deliveryChannels: opts.deliveryChannels } : {})
            }
        });
    }
    /**
     * Ring someone into a live call (client→client only). Any joined
     * participant may invite, by saved account, number, @handle or email;
     * the answer is conference_invite_sent or an error with operation
     * 'conference_add'.
     */
    conferenceAdd(callId, target) {
        this.sendWhenAuthenticated({
            type: 'conference_add',
            data: { callId, ...target }
        });
    }
    sendP2PCall(target) {
        const phoneNumber = normalizeP2PPhoneTarget(target);
        this.sendWhenAuthenticated({
            type: 'p2p_call',
            data: phoneNumber ? { phoneNumber } : { target }
        });
    }
    /**
     * In-call green button: request an interpreter into the live P2P room, in
     * the caller's sign language. P2P calls record none, so without it the
     * server falls back to ASL.
     */
    sendP2PRequestInterpreter(callId, roomName, requestId, target = {}) {
        const data = {
            callId,
            roomName,
            requestId
        };
        if (target.language) {
            data.language = target.language;
        }
        if (target.serviceRegion) {
            data.serviceRegion = target.serviceRegion;
        }
        this.sendWhenAuthenticated({
            type: 'p2p_request_interpreter',
            data
        });
    }
    /**
     * Call a client, from the interpreter side.
     *
     * The mirror of a client requesting an interpreter, and the one interpreter
     * workflow that existed on web and nowhere else — Malka Terp could answer a
     * call but never place one, so an interpreter who needed to reconnect with
     * a client had to go and find a browser.
     *
     * The server resolves the target against client accounts only, by public
     * handle or assigned VRS number, and fails closed for busy, DND or
     * unreachable. One field either way: it accepts a handle or a number and
     * works out which it is.
     */
    sendInterpreterCallClient(target) {
        this.sendWhenAuthenticated({
            type: 'interpreter_call_client',
            data: { target: target.trim() }
        });
    }
    sendGroupCall(participants, roomName) {
        this.sendWhenAuthenticated({
            type: 'group_call',
            data: { participants, roomName }
        });
    }
    clearConferenceResponseTimer(key) {
        const timer = this.conferenceResponseTimers.get(key);
        if (timer)
            clearTimeout(timer);
        this.conferenceResponseTimers.delete(key);
    }
    clearConferenceResponsesForCall(callId) {
        if (!callId)
            return;
        for (const key of this.conferenceResponses.keys()) {
            if (key.startsWith(`${callId}:`)) {
                this.clearConferenceResponseTimer(key);
                this.conferenceResponses.delete(key);
            }
        }
        this.pendingAuthedMessages = this.pendingAuthedMessages.filter(message => message.type !== 'conference_respond' || recordPayload(message.data).callId !== callId);
    }
    rememberConferenceResponse(callId, invitationId, accept) {
        const key = `${callId}:${invitationId}`;
        this.clearConferenceResponseTimer(key);
        this.conferenceResponses.set(key, accept);
        // A newer response supersedes an unsent one after a connection loss.
        this.pendingAuthedMessages = this.pendingAuthedMessages.filter(message => message.type !== 'conference_respond' || recordPayload(message.data).callId !== callId
            || recordPayload(message.data).invitationId !== invitationId);
        if (accept) {
            this.conferenceResponseTimers.set(key, setTimeout(() => {
                this.conferenceResponseTimers.delete(key);
                this.emit('conferenceInvitationError', { callId, invitationId, code: 'RESPONSE_TIMEOUT',
                    message: 'Your answer has not been confirmed. Check your connection and try again.' });
            }, 10000));
        }
        if (this.conferenceResponses.size > 64) {
            const oldest = this.conferenceResponses.keys().next().value;
            if (oldest) {
                this.clearConferenceResponseTimer(oldest);
                this.conferenceResponses.delete(oldest);
            }
        }
    }
    acceptP2PCall({ callId, callerId, roomName, conferenceInvitation, invitationId }) {
        if (conferenceInvitation) {
            if (!invitationId)
                throw new Error('This invitation is incomplete. Ask the caller to invite you again.');
            this.rememberConferenceResponse(callId, invitationId, true);
            try {
                this.sendWhenAuthenticated({ type: 'conference_respond', data: { callId, invitationId, accept: true } });
            }
            catch (error) {
                this.clearConferenceResponseTimer(`${callId}:${invitationId}`);
                throw error;
            }
            return;
        }
        this.sendWhenAuthenticated({
            type: 'p2p_accept',
            data: { callId, callerId, roomName }
        });
    }
    declineP2PCall({ callId, callerId, sendToVoicemail, conferenceInvitation, invitationId, reason }) {
        if (conferenceInvitation) {
            if (!invitationId)
                throw new Error('This invitation is incomplete.');
            const cancelPendingAcceptance = this.conferenceResponses.get(`${callId}:${invitationId}`) === true;
            this.rememberConferenceResponse(callId, invitationId, false);
            this.sendWhenAuthenticated({ type: 'conference_respond', data: { callId, invitationId, accept: false, cancelPendingAcceptance } });
            return;
        }
        this.sendWhenAuthenticated({
            type: 'p2p_decline',
            data: reason ? { callId, callerId, sendToVoicemail, reason } : { callId, callerId, sendToVoicemail }
        });
    }
    prepareVriInvite(data) {
        this.sendWhenAuthenticated({
            type: 'prepare_vri_invite',
            data
        });
    }
    registerHandoffSession(roomName, deviceId) {
        // Handoff sessions belong to clients; interpreters only receive transfer notices.
        if (this.userRole !== 'client' || !roomName || !deviceId) {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'session_register',
            data: {
                roomName,
                deviceId
            }
        });
    }
    unregisterHandoffSession() {
        if (this.userRole !== 'client') {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'session_unregister',
            data: {
                deviceId: getHandoffDeviceId()
            }
        });
    }
    /**
     * Whether this call moved to another device of this client account. A
     * device the call moved away from leaves without ending it.
     */
    hasCallMovedAway(callId) {
        return Boolean(callId && this.movedAwayCallIds.has(callId));
    }
    /** Ends a call that has no local room record, e.g. an external-meeting call. */
    endCall(callId, roomName) {
        if (this.hasCallMovedAway(callId)) {
            mobileLog('info', 'queue_call_end_suppressed_moved_away', {}, { console: false });
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_end',
            data: { callId, ...(roomName ? { roomName } : {}) }
        });
    }
    /**
     * The host ends a group call for everyone (p2p_end_for_all). Sent only on
     * a live, authenticated socket, never buffered: a request replayed after
     * a reconnect could end the call long after the host was told it had not.
     *
     * @returns {boolean} Whether the request went out.
     */
    endGroupCallForAll(callId, roomName) {
        if (!this.authenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            mobileLog('warn', 'queue_ws_send_skipped', { messageType: 'p2p_end_for_all', reason: 'not_connected' }, { console: false });
            return false;
        }
        this.ws.send(JSON.stringify({ type: 'p2p_end_for_all', data: { callId, ...(roomName ? { roomName } : {}) } }));
        mobileLog('info', 'queue_ws_authed_message_sent', { messageType: 'p2p_end_for_all' }, { console: false });
        return true;
    }
    /**
     * Stop ringing one invitee (conference_invite_cancel): the inviter, or
     * the host, while the ring is still ringing. Answered with
     * conference_invite_result { status: 'cancelled' } or an error with
     * operation 'conference_invite_cancel'. Sent only on a live socket, like
     * ending for everyone: a cancel replayed after a reconnect would be about
     * a ring that has long since changed.
     *
     * @returns {boolean} Whether the request went out.
     */
    cancelConferenceInvite(callId, targetId) {
        if (!this.authenticated || !this.ws || this.ws.readyState !== WebSocket.OPEN) {
            mobileLog('warn', 'queue_ws_send_skipped', { messageType: 'conference_invite_cancel', reason: 'not_connected' }, { console: false });
            return false;
        }
        this.ws.send(JSON.stringify({ type: 'conference_invite_cancel', data: { callId, targetId } }));
        mobileLog('info', 'queue_ws_authed_message_sent', { messageType: 'conference_invite_cancel' }, { console: false });
        return true;
    }
    endActiveCall() {
        const activeCall = getStoredJson('vrs_active_call');
        if (!activeCall?.callId) {
            return;
        }
        if (this.hasCallMovedAway(activeCall.callId)) {
            mobileLog('info', 'queue_call_end_suppressed_moved_away', {}, { console: false });
            removePersistentItem('vrs_active_call');
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_end',
            data: {
                callId: activeCall.callId,
                roomName: activeCall.roomName
            }
        });
        removePersistentItem('vrs_active_call');
    }
    /**
     * Tell the server this device's media connection for the call is up.
     * Completes a move of the client's call to this device: only now may the
     * other device leave without the interpreter's client pane going blank.
     */
    notifyCallRoomJoined(callId) {
        if (!callId) {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_room_joined',
            data: { callId }
        });
    }
    /**
     * This device could not join the call it was moving to (for example the
     * room is full). The server keeps the call on the other device.
     */
    notifyCallMoveFailed(callId, reason) {
        if (!callId) {
            return;
        }
        this.sendWhenAuthenticated({
            type: 'call_move_failed',
            data: { callId, reason }
        });
    }
    /** Whether the server is holding this session out (another device is in a call). */
    isHeldElsewhere() {
        return this.heldElsewhere;
    }
    /**
     * While held out, ask again whether the other device's call has ended.
     * Answered by account_call_status. Sent on the open, unauthenticated
     * socket; never re-authenticates.
     */
    requestAccountCallStatus() {
        this.send({ type: 'account_call_status' });
    }
    /**
     * The user chose to use this device after the other device's call ended:
     * authenticate again. The server supersedes the other device if its call
     * is over, or holds this session out again if it is not.
     */
    retakeSession() {
        this.heldElsewhere = false;
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.sendAuth();
            return;
        }
        this.reconnect();
    }
    send(message) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
        }
        else {
            const now = Date.now();
            if (this.lastSendWarningLogAt === 0 || now - this.lastSendWarningLogAt >= SEND_WARNING_LOG_INTERVAL) {
                this.lastSendWarningLogAt = now;
                mobileLog('warn', 'queue_ws_send_skipped', {
                    messageType: message.type,
                    reason: 'not_connected',
                    url: this.config.queueServiceUrl
                }, { console: false });
            }
        }
    }
    /**
     * Sends a message that the server only accepts from an authenticated
     * socket. When the connection is still opening or awaiting auth_success
     * (e.g. cold app start, post-refresh re-auth), the message is buffered
     * and flushed as soon as authentication completes — instead of being
     * silently dropped while the UI shows a pending state.
     */
    sendWhenAuthenticated(message) {
        if (this.authenticated && this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(message));
            mobileLog('info', 'queue_ws_authed_message_sent', {
                messageType: message.type,
                wsReadyState: this.ws.readyState
            }, { console: false });
            return;
        }
        if (this.pendingAuthedMessages.length >= PENDING_AUTHED_MESSAGE_LIMIT) {
            this.pendingAuthedMessages.shift();
        }
        this.pendingAuthedMessages.push(message);
        mobileLog('info', 'queue_ws_message_buffered_for_auth', {
            messageType: message.type
        }, { console: false });
        // Held out by the server: re-sending auth would only be held out
        // again (and hammer it). The message waits for retakeSession().
        if (this.heldElsewhere) {
            return;
        }
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            mobileLog('info', 'queue_ws_reauth_for_buffered_message', {
                messageType: message.type,
                pendingCount: this.pendingAuthedMessages.length
            }, { console: false });
            this.sendAuth();
            return;
        }
        if (!this.ws || this.ws.readyState === WebSocket.CLOSED || this.ws.readyState === WebSocket.CLOSING) {
            this.connect();
        }
    }
    flushPendingAuthedMessages() {
        while (this.pendingAuthedMessages.length > 0 && this.ws && this.ws.readyState === WebSocket.OPEN) {
            const message = this.pendingAuthedMessages.shift();
            if (message) {
                this.ws.send(JSON.stringify(message));
                mobileLog('info', 'queue_ws_buffered_message_flushed', {
                    messageType: message.type,
                    remaining: this.pendingAuthedMessages.length
                }, { console: false });
            }
        }
    }
    isConnected() {
        return this.ws?.readyState === WebSocket.OPEN;
    }
    getUserId() {
        return this.userId;
    }
    canAuthenticate() {
        return hasQueueAuthForRole(getUserRole());
    }
    closeSocket() {
        const socket = this.ws;
        this.ws = null;
        if (!socket)
            return;
        socket.onopen = null;
        socket.onmessage = null;
        socket.onclose = null;
        socket.onerror = null;
        try {
            socket.close();
        }
        catch {
            mobileLog('warn', 'queue_ws_close_failed', {}, { console: false });
        }
    }
    disconnect() {
        this.shouldReconnect = false;
        this.authenticated = false;
        this.pendingAuthedMessages = [];
        for (const key of this.conferenceResponseTimers.keys())
            this.clearConferenceResponseTimer(key);
        this.conferenceResponses.clear();
        this.stopHeartbeat();
        this.clearStabilityTimer();
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        this.closeSocket();
        this.presenceEventCleanup?.();
        this.presenceEventCleanup = null;
        this.listeners.clear();
    }
    reconnect() {
        this.shouldReconnect = true;
        this.stopHeartbeat();
        // An explicit reconnect is fresh user intent (foregrounding, toggling
        // availability): forgive the exhausted automatic-retry counter so the
        // attempt ladder actually runs again.
        this.reconnectAttempts = 0;
        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout);
            this.reconnectTimeout = null;
        }
        this.closeSocket();
        this.clearStabilityTimer();
        this.authenticated = false;
        this.connect();
    }
    ping() {
        this.send({ type: 'heartbeat', data: getPresenceHeartbeatPayload() });
    }
}
const hasWebSocketRuntime = typeof WebSocket !== 'undefined';
let _queueService = null;
function getQueueServiceInstance() {
    if (!hasWebSocketRuntime) {
        return null;
    }
    if (!_queueService) {
        _queueService = new InterpreterQueueService();
    }
    return _queueService;
}
export const queueService = {
    get instance() {
        return getQueueServiceInstance();
    },
    on: (event, callback) => queueService.instance.on(event, callback),
    off: (event, callback) => queueService.instance.off(event, callback),
    isConnected: () => Boolean(getQueueServiceInstance()?.isConnected()),
    canAuthenticate: () => Boolean(getQueueServiceInstance()?.canAuthenticate()),
    getUserId: () => getQueueServiceInstance()?.getUserId() || null,
    disconnect: () => getQueueServiceInstance()?.disconnect(),
    reconnect: () => getQueueServiceInstance()?.reconnect(),
    ping: () => getQueueServiceInstance()?.ping(),
    send: (...args) => getQueueServiceInstance()?.send(...args),
    updateInterpreterStatus: (...args) => getQueueServiceInstance()?.updateInterpreterStatus(...args),
    getLastInterpreterStatus: () => getQueueServiceInstance()?.getLastInterpreterStatus() || null,
    holdAvailabilityReplay: () => getQueueServiceInstance()?.holdAvailabilityReplay(),
    requestInterpreter: (...args) => getQueueServiceInstance()?.requestInterpreter(...args),
    endActiveCall: () => getQueueServiceInstance()?.endActiveCall(),
    endCall: (callId, roomName) => getQueueServiceInstance()?.endCall(callId, roomName),
    endGroupCallForAll: (callId, roomName) => getQueueServiceInstance()?.endGroupCallForAll(callId, roomName) === true,
    cancelConferenceInvite: (callId, targetId) => getQueueServiceInstance()?.cancelConferenceInvite(callId, targetId) === true,
    sendP2PCall: (...args) => getQueueServiceInstance()?.sendP2PCall(...args),
    sendGroupCall: (...args) => getQueueServiceInstance()?.sendGroupCall(...args),
    sendP2PRequestInterpreter: (...args) => getQueueServiceInstance()?.sendP2PRequestInterpreter(...args),
    announceLocalRoomJoin: (...args) => getQueueServiceInstance()?.announceLocalRoomJoin(...args),
    sendInterpreterCallClient: (...args) => getQueueServiceInstance()?.sendInterpreterCallClient(...args),
    acceptP2PCall: (...args) => getQueueServiceInstance()?.acceptP2PCall(...args),
    declineP2PCall: (...args) => getQueueServiceInstance()?.declineP2PCall(...args),
    prepareVriInvite: (...args) => getQueueServiceInstance()?.prepareVriInvite(...args),
    registerHandoffSession: (...args) => getQueueServiceInstance()?.registerHandoffSession(...args),
    unregisterHandoffSession: (...args) => getQueueServiceInstance()?.unregisterHandoffSession(...args),
    cancelRequest: (...args) => getQueueServiceInstance()?.cancelRequest(...args),
    acceptRequest: (...args) => getQueueServiceInstance()?.acceptRequest(...args),
    declineRequest: (...args) => getQueueServiceInstance()?.declineRequest(...args),
    transferCall: (...args) => getQueueServiceInstance()?.transferCall(...args),
    prepareRoomInvite: (...args) => getQueueServiceInstance()?.prepareRoomInvite(...args),
    ringClientAgain: (...args) => getQueueServiceInstance()?.ringClientAgain(...args),
    conferenceAdd: (...args) => getQueueServiceInstance()?.conferenceAdd(...args),
    notifyCallRoomJoined: (callId) => getQueueServiceInstance()?.notifyCallRoomJoined(callId),
    notifyCallMoveFailed: (callId, reason) => getQueueServiceInstance()?.notifyCallMoveFailed(callId, reason),
    requestAccountCallStatus: () => getQueueServiceInstance()?.requestAccountCallStatus(),
    retakeSession: () => getQueueServiceInstance()?.retakeSession(),
    isHeldElsewhere: () => Boolean(getQueueServiceInstance()?.isHeldElsewhere()),
    hasCallMovedAway: (callId) => Boolean(getQueueServiceInstance()?.hasCallMovedAway(callId))
};
export default InterpreterQueueService;
